import { useState } from 'react';
import { AlertTriangle, Calendar, ChevronDown, Clock, GripVertical } from 'lucide-react';
import { bookingFor } from '../lib/adapters';
import { formatTaskDate, formatTaskTime, getTaskDataCells } from '../lib/dataCells';
import type { CargoFamily, MockBooking, MockTask, TaskPanelContext } from '../lib/types';
import { BookingMetadataEditPanel } from './BookingMetadataEditPanel';
import { CountryBadge, DataCell, TaskTypeBadge } from './badges';
import { taskTypeLabel } from '../lib/taskTypeConfig';
import { PickupDeliveryForm } from './PickupDeliveryForm';
import { TaskAssignmentPanel } from './TaskAssignmentPanel';
import { TaskStateMenu, TaskStatePill } from './TaskStateControls';


interface TaskCardProps {
  task: MockTask;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
  /** 1 or 2 — per-unit row count for 2PT variants (Container 20ft, ISO Tank 20ft). */
  bicCount: number;
  /** Swaps the data-strip column set (container / tank / pallet). */
  cargoFamily: CargoFamily;
  /** All bookings — used by BookingTaskForm to resolve task.bookingId. */
  bookings: MockBooking[];
  /** Order-level shared context (addresses, service tags, cargo family, perTrailer). */
  panelContext: TaskPanelContext;
  /**
   * True when this task's planned window closes before an earlier task
   * in the list can start. Advisory only — see `lib/taskOrder.ts` for
   * why chronology never reorders the list on its own.
   */
  hasTimeConflict: boolean;
  /** Reorder wiring. Absent while a resequence write is in flight. */
  drag?: TaskDragHandlers;
}

/**
 * Drop-target + drag-source wiring for one card. Kept as one object so
 * the panel can pass `undefined` to disable reordering wholesale (e.g.
 * while a resequence is saving) without nulling six props.
 */
export interface TaskDragHandlers {
  /** Where the drop indicator sits on this card, if anywhere. */
  dropEdge: 'above' | 'below' | null;
  /** True while THIS card is the one being dragged. */
  isSource: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  /** `before` = insert above this card, `after` = below. */
  onDragOverEdge: (edge: 'above' | 'below') => void;
  onDragLeave: () => void;
  onDrop: () => void;
}

/** One row in the task accordion. Collapsed = summary strip; expanded = edit form. */
export function TaskCard({
  task,
  index,
  isOpen,
  onToggle,
  bicCount,
  cargoFamily,
  bookings,
  panelContext,
  hasTimeConflict,
  drag,
}: TaskCardProps) {
  const dataCells = getTaskDataCells(task, bicCount, cargoFamily, panelContext, bookingFor(task, bookings));
  const isTwoRow = bicCount === 2 && dataCells.some((c) => c.perUnit);
  const spanLeftRight = isTwoRow ? 'row-span-2' : '';

  // HTML5 drag has to be armed on an ancestor of the grip for the drag
  // image to be the whole card rather than the handle alone — but a
  // permanently `draggable` card also hijacks text selection in the
  // title. So the grip arms it on mousedown and disarms on drag end.
  const [dragArmed, setDragArmed] = useState(false);

  // Reordering is gated on the card being collapsed: dragging a row
  // with an open form (and possibly unsaved edits) reads as a bug.
  const canDrag = !!drag && !isOpen;

  const dropEdgeClass =
    drag?.dropEdge === 'above'
      ? 'border-t-2 border-t-purple-500'
      : drag?.dropEdge === 'below'
        ? 'border-b-2 border-b-purple-500'
        : '';

  return (
    <div
      draggable={canDrag && dragArmed}
      onDragStart={(e) => {
        // Firefox ignores drags with no payload.
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', task.id);
        drag?.onDragStart();
      }}
      onDragEnd={() => {
        setDragArmed(false);
        drag?.onDragEnd();
      }}
      onDragOver={(e) => {
        if (!drag || drag.isSource) return;
        // Without preventDefault the browser refuses the drop outright.
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const box = e.currentTarget.getBoundingClientRect();
        drag.onDragOverEdge(e.clientY < box.top + box.height / 2 ? 'above' : 'below');
      }}
      onDragLeave={() => drag?.onDragLeave()}
      onDrop={(e) => {
        if (!drag || drag.isSource) return;
        e.preventDefault();
        drag.onDrop();
      }}
      className={
        'col-span-full grid grid-cols-subgrid rounded-md border transition-colors ' +
        (isOpen
          ? 'border-purple-300 bg-purple-50/40 shadow-sm'
          : 'border-slate-200 bg-white hover:border-slate-300') +
        (drag?.isSource ? ' opacity-40' : '') +
        ' ' +
        dropEdgeClass
      }
    >
      {/* A div with button semantics rather than a real <button>: the row now
          carries its own controls (the status menu), and a button inside a
          button is invalid HTML — browsers un-nest it and the inner clicks stop
          working. The keyboard behaviour is kept by hand. */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className="col-span-full grid cursor-pointer grid-cols-subgrid items-center gap-y-1 px-3 py-2 text-left"
      >
        {/* Position badge doubles as the drag grip so reordering costs no
            extra grid column — the panel's column template is explicit. */}
        <span
          onMouseDown={() => canDrag && setDragArmed(true)}
          onMouseUp={() => setDragArmed(false)}
          title={canDrag ? 'Drag to reorder' : undefined}
          className={
            'group/grip flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-slate-100 text-[11px] font-semibold text-slate-700 ' +
            (canDrag ? 'cursor-grab hover:bg-slate-200 active:cursor-grabbing ' : '') +
            spanLeftRight
          }
        >
          {canDrag ? (
            <>
              <span className="group-hover/grip:hidden">{index + 1}</span>
              <GripVertical className="hidden h-3.5 w-3.5 text-slate-500 group-hover/grip:block" />
            </>
          ) : (
            index + 1
          )}
        </span>
        <TaskTypeBadge
          type={task.type}
          label={task._source ? taskTypeLabel(task._source.type) : undefined}
          className={spanLeftRight}
        />
        <p className={'min-w-0 truncate text-sm ' + spanLeftRight}>
          <span className="font-semibold text-slate-900">{task.title}</span>
          {task.subtitle ? (
            <span className="font-normal text-slate-500">, {task.subtitle}</span>
          ) : null}
        </p>
        {task.countryCode ? (
          <CountryBadge code={task.countryCode} className={spanLeftRight} />
        ) : (
          <span className={spanLeftRight} />
        )}
        <span
          className={
            'flex flex-col gap-0.5 whitespace-nowrap text-xs text-slate-500 ' + spanLeftRight
          }
        >
          <span className="inline-flex items-center gap-1.5">
            {hasTimeConflict ? (
              <span
                className="inline-flex shrink-0"
                title="Planned earlier than a task above it — reorder, or fix the date."
              >
                <AlertTriangle className="h-3 w-3 text-amber-500" />
              </span>
            ) : (
              <Calendar className="h-3 w-3 shrink-0 text-slate-400" />
            )}
            <span className={hasTimeConflict ? 'font-medium text-amber-700' : undefined}>
              {formatTaskDate(task.plannedDate)}
            </span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3 w-3 shrink-0 text-slate-400" />
            <span>{formatTaskTime(task)}</span>
          </span>
        </span>
        {dataCells.map((cell) => (
          <DataCell key={cell.label} label={cell.label} value={cell.values[0]} />
        ))}
        {/* Status, actions and the expander share the trailing column, so the
            row's own `items-center` centres them — no overlay, and the strip's
            explicit grid template, which is written out twice for two layouts,
            is left alone. */}
        <span
          className={
            'flex shrink-0 items-center justify-end gap-1.5 [grid-column:-2/-1] ' + spanLeftRight
          }
          // The controls are not the expander: a click on either must not also
          // open the form underneath them.
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          <TaskStatePill state={task.state} />
          {task._source?.orderId && (
            <TaskStateMenu
              orderId={task._source.orderId}
              taskId={task.id}
              taskLabel={`${task.title} (task ${index + 1})`}
              state={task._source.state}
            />
          )}
          <ChevronDown
            className={
              'h-4 w-4 shrink-0 cursor-pointer text-slate-400 transition-transform ' +
              (isOpen ? 'rotate-180' : '')
            }
            onClick={onToggle}
          />
        </span>
        {isTwoRow &&
          dataCells.map((cell) =>
            cell.perUnit ? (
              <DataCell key={`u2-${cell.label}`} value={cell.values[1]} />
            ) : (
              <span key={`u2-${cell.label}`} />
            ),
          )}
      </div>
      {isOpen && (
        <div className="col-span-full space-y-3 border-t border-purple-200 p-3">
          {/* Read-only crew line, above the type-specific form and outside it,
              because every task type can be assigned — including booking halves
              and process events, which have no cargo form at all. Renders nothing
              when the task is unassigned. */}
          {task._source?.orderId && (
            <TaskAssignmentPanel orderId={task._source.orderId} taskId={task.id} />
          )}
          <TaskForm task={task} bookings={bookings} panelContext={panelContext} onClose={onToggle} />
        </div>
      )}
    </div>
  );
}

function TaskForm({
  task,
  bookings,
  panelContext,
  onClose,
}: {
  task: MockTask;
  bookings: MockBooking[];
  panelContext: TaskPanelContext;
  onClose: () => void;
}) {
  if (task.type === 'BookingDeparture' || task.type === 'BookingArrival') {
    return (
      <BookingTaskForm
        task={task}
        bookings={bookings}
        panelContext={panelContext}
        onClose={onClose}
      />
    );
  }
  return <PickupDeliveryForm task={task} panelContext={panelContext} onClose={onClose} />;
}

/**
 * Expanded form for one half of a ferry booking. Shows the booking
 * metadata chip (read-only or editable) plus the per-port planned /
 * actual time fields and notes.
 */
function BookingTaskForm({
  task,
  bookings,
  panelContext,
  onClose,
}: {
  task: MockTask;
  bookings: MockBooking[];
  panelContext: TaskPanelContext;
  onClose: () => void;
}) {
  const booking = bookingFor(task, bookings);

  // No booking yet — nothing to show in the expanded form.
  if (!booking) return null;

  return (
    <BookingMetadataEditPanel
      booking={booking}
      orderId={task._source?.orderId ?? ''}
      addresses={panelContext.addresses}
      organisationId={panelContext.organisationId}
      onClose={onClose}
    />
  );
}
